import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@material-ui/core';
import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { translate } from '../../../../common/intl';
import { AsyncState, AsyncStatus } from '../../../../state/types';
import { useStyles } from './WhiteLabelingManagement.styles';

interface Props extends FormikProps<any>{
  onLogoChange: (event: React.FormEvent<HTMLInputElement>) => void
  onAvatarChange: (event: React.FormEvent<HTMLInputElement>) => void
  onPoLogoChange: (event: React.FormEvent<HTMLInputElement>) => void
  onPoLogoSameAsLogo: (checked: boolean) => void
  asyncState: AsyncState
  whiteLabel: { logo: string, avatar: string, poLogo?: string }
}

interface FileUploadInfo {
  name: string
  size: string
}

const WhiteLabelingManagementPresenter: React.FunctionComponent<Props> = ({
  dirty,
  resetForm,
  initialValues,
  onLogoChange,
  onAvatarChange,
  onPoLogoChange,
  onPoLogoSameAsLogo,
  asyncState,
  whiteLabel,
}) => {
  const classes = useStyles();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const poLogoInputRef = useRef<HTMLInputElement>(null);
  const [logoUploadedFile, setLogoUploadedFile] = useState<FileUploadInfo | null>(null);
  const [avatarUploadedFile, setAvatarUploadedFile] = useState<FileUploadInfo | null>(null);
  const [poLogoUploadedFile, setPoLogoUploadedFile] = useState<FileUploadInfo | null>(null);
  const [isPoLogoSameAsLogo, setIsPoLogoSameAsLogo] = useState(false);

  const handlePoLogoSameAsLogoChange = (checked: boolean) => {
    setIsPoLogoSameAsLogo(checked);
    onPoLogoSameAsLogo(checked);
  };

  const handleUploadClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    inputRef.current?.click();
  };

  const createFileChangeHandler = (
    setUploadedFile: React.Dispatch<React.SetStateAction<FileUploadInfo | null>>,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
  ) => {
    return (event: React.FormEvent<HTMLInputElement>) => {
      const file = event.currentTarget.files?.[0];
      if (file) {
        const fileSizeKB = (file.size / 1024).toFixed(0);
        setUploadedFile({
          name: file.name,
          size: `${fileSizeKB} KB`,
        });
      }
      onChange(event);
    };
  };

  const getFileExtension = (fileName: string) => {
    return fileName.split('.').pop()?.toUpperCase() || 'FILE';
  };

  const isLoading = asyncState?.status === AsyncStatus.Active;

  const renderUploadSection = (
    title: string,
    uploadedFile: FileUploadInfo | null,
    inputRef: React.RefObject<HTMLInputElement>,
    onChange: (event: React.FormEvent<HTMLInputElement>) => void,
    s3link: string | undefined,
    setUploadedFile: React.Dispatch<React.SetStateAction<FileUploadInfo | null>>,
  ) => {
    const handleFileChange = createFileChangeHandler(
      setUploadedFile,
      onChange,
    );

    return (
      <Box mb={2}>
        <Typography className={classes.label}>
          {title}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,.svg"
          onChange={handleFileChange}
          className={classes.fileInput}
          title="Choose file"
          aria-label="Choose file"
          disabled={isLoading}
        />

        {uploadedFile ? (
          <Box>
            <Box className={classes.fileInfo}>
              <Box className={classes.fileIcon}>
                {isLoading ? (
                  <CircularProgress size={16} thickness={4} />
                ) : (
                  getFileExtension(uploadedFile.name)
                )}
              </Box>
              <Box className={classes.fileDetails}>
                <Typography className={classes.fileName}>
                  {uploadedFile.name}
                </Typography>
                <Typography className={classes.fileSize}>
                  {uploadedFile.size}
                </Typography>
              </Box>
            </Box>
            {!isLoading && (
              <Box
                className={classes.uploadArea}
                onClick={() => handleUploadClick(inputRef)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleUploadClick(inputRef);
                  }
                }}
                aria-label={`Replace ${title.toLowerCase()}`}
              >
                <Typography className={classes.uploadText}>
                  Replace
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            className={classes.uploadArea}
            onClick={() => handleUploadClick(inputRef)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleUploadClick(inputRef);
              }
            }}
            aria-label={`Upload white labeling ${title.toLowerCase()}`}
          >
            <Typography className={classes.uploadIcon}>📁</Typography>
            <Typography className={classes.uploadText}>
              Drag, drop or choose a {title.toLowerCase()} file
            </Typography>
            <Typography className={classes.uploadSubtext}>
              JPG, PNG or SVG
            </Typography>
          </Box>
        )}

        {s3link && (
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              S3 URL:
            </Typography>
            <Typography
              variant="body2"
              style={{
                wordBreak: 'break-all',
                marginTop: 8,
              }}
            >
              {s3link}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {renderUploadSection(
        translate('administration.company.information.whiteLabeling.logo.upload'),
        logoUploadedFile,
        logoInputRef,
        onLogoChange,
        whiteLabel.logo,
        setLogoUploadedFile,
      )}
      {renderUploadSection(
        translate('administration.company.information.whiteLabeling.avatar.upload'),
        avatarUploadedFile,
        avatarInputRef,
        onAvatarChange,
        whiteLabel.avatar,
        setAvatarUploadedFile,
      )}
      <Box>
        {renderUploadSection(
          translate('administration.company.information.whiteLabeling.poLogo.upload'),
          poLogoUploadedFile,
          poLogoInputRef,
          onPoLogoChange,
          whiteLabel.poLogo,
          setPoLogoUploadedFile,
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={isPoLogoSameAsLogo}
              onChange={(event) => handlePoLogoSameAsLogoChange(event.target.checked)}
              disabled={isLoading || !whiteLabel.logo}
            />
          }
          label={translate('administration.company.information.whiteLabeling.poLogo.sameAsLogo')}
        />
      </Box>
    </Box>
  );
};

export default WhiteLabelingManagementPresenter;
